/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: DialProfileFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Dial Profiles module.
----------------------------------------------------------------*/


import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PhoneCall, Plus, RefreshCw, Search, Copy } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { ModuleFormHeader } from '../../components/layouts';
import { FormBox } from '../../components/ui/FormBox';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import Toggle from '../../components/ui/Toggle';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { navigateToDialProfileDuplicate } from './dialProfilesActions';
import {
  fetchDialProfile,
  createDialProfile,
  updateDialProfile,
  deleteDialProfile,
  fetchDialProfiles,
} from './api';
import { DialProfile } from './types';
import { DialProfileGeneralSection } from './components/DialProfileGeneralSection';
import type { DialProfileFormData, AllowByValue, CallScreeningValue } from './components/DialProfileGeneralSection';



const DEFAULT_FORM_DATA: DialProfileFormData = {
  name: '',
  allowTransferBy: 'recipient',
  allowParkBy: 'recipient',
  ringbackTone: 'default',
  callScreening: 'disabled',
  customOptions: '',
  ringingTone: true,
  description: '',
  active: true,
};

const RINGBACK_PRESETS = ['default', 'none', 'test', 'music/default'] as const;

const sanitizeFormData = (data: DialProfileFormData): DialProfileFormData => ({
  ...data,
  name: data.name.trim(),
  ringbackTone: data.ringbackTone.trim(),
  customOptions: data.customOptions.replace(/\s+$/gm, '').trim(),
  description: data.description.trim(),
});

const deepClone = <T,>(value: T): T =>
  typeof structuredClone === 'function'
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value));

export default function DialProfileFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const location = useLocation();
  const isEditMode = Boolean(id && id !== 'new');

  const navigate = useNavigate();
  const { t } = useI18n();
  const { notifications, removeNotification, showError, showSuccess, showInfo } = useNotifications();

  const isSettingsContext = location.pathname.startsWith('/settings/technology/dial-profile');
  const basePath = isSettingsContext ? '/settings/technology/dial-profile' : '/pbx/applications/dial-profiles';

  const [formData, setFormData] = useState<DialProfileFormData>(deepClone(DEFAULT_FORM_DATA));
  const [originalData, setOriginalData] = useState<DialProfileFormData>(deepClone(DEFAULT_FORM_DATA));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(isEditMode || Boolean(duplicateId));
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDefaultDialProfile, setIsDefaultDialProfile] = useState(false);
  const lastSaveTimestamp = useRef(0);
  const tRef = useRef(t);

  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  // Quick List states
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListProfiles, setQuickListProfiles] = useState<DialProfile[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const pageTitle = useMemo(
    () => (isEditMode ? `${t('dialProfiles.editEntry')} - ${formData.name || ''}` : t('dialProfiles.createEntry')),
    [isEditMode, t, formData.name],
  );

  useEffect(() => {
    document.title = `${pageTitle} | Ring2All`;
  }, [pageTitle]);

  const selectedRingbackPreset = useMemo(() => {
    if (RINGBACK_PRESETS.includes(formData.ringbackTone as typeof RINGBACK_PRESETS[number])) {
      return formData.ringbackTone;
    }
    if (!formData.ringbackTone) {
      return 'default';
    }
    return 'custom';
  }, [formData.ringbackTone]);

  const customRingbackValue = selectedRingbackPreset === 'custom' ? formData.ringbackTone : '';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const applyLoadedData = useCallback((payload: any, { duplicate }: { duplicate: boolean } = { duplicate: false }) => {
    const copySuffix = t('common.copySuffix', 'Copy');
    const adapted: DialProfileFormData = {
      name: duplicate ? `${payload?.name ?? ''} ${copySuffix}`.trim() : (payload?.name ?? ''),
      allowTransferBy: (payload?.allowTransferBy ?? payload?.allow_transfer_by ?? 'recipient') as AllowByValue,
      allowParkBy: (payload?.allowParkBy ?? payload?.allow_park_by ?? 'recipient') as AllowByValue,
      ringbackTone: payload?.ringbackTone ?? payload?.ringback_tone ?? 'default',
      callScreening: (payload?.callScreening ?? payload?.call_screening ?? 'disabled') as CallScreeningValue,
      customOptions: payload?.customOptions ?? payload?.custom_options ?? '',
      ringingTone:
        payload?.ringingTone !== undefined
          ? Boolean(payload.ringingTone)
          : payload?.ringing_tone !== undefined
            ? Boolean(payload.ringing_tone)
            : true,
      description: payload?.description ?? '',
      active:
        payload?.active !== undefined
          ? Boolean(payload.active)
          : payload?.isActive !== undefined
            ? Boolean(payload.isActive)
            : true,
    };

    setFormData(adapted);
    setIsDefaultDialProfile(duplicate ? false : Boolean(payload?.isDefault ?? payload?.is_default));
    if (duplicate) {
      setOriginalData(deepClone(DEFAULT_FORM_DATA));
      showInfo(t('dialProfiles.messages.duplicateHint'));
    } else {
      setOriginalData(deepClone(adapted));
    }
    setErrors({});
  }, [t, showInfo]);

  const loadDialProfile = useCallback(async (entryId: string, { duplicate }: { duplicate: boolean }) => {
    setIsLoading(true);
    try {
      const result = await fetchDialProfile(entryId);
      if (!result?.success || !result?.data) {
        throw new Error('Failed to load dial profile');
      }
      applyLoadedData(result.data, { duplicate });
    } catch (error) {
      console.error('Failed to load dial profile', error);
      showError(tRef.current('dialProfiles.messages.loadError'));
      navigate(basePath);
    } finally {
      setIsLoading(false);
    }
  }, [applyLoadedData, basePath, navigate, showError]);

  // Store stable references to avoid re-running the fetch effect
  const loadDialProfileRef = useRef(loadDialProfile);
  const applyLoadedDataRef = useRef(applyLoadedData);

  // Update refs when values change (but don't trigger effects)
  useEffect(() => {
    loadDialProfileRef.current = loadDialProfile;
    applyLoadedDataRef.current = applyLoadedData;
  }, [loadDialProfile, applyLoadedData]);

  useEffect(() => {
    if (isEditMode && id) {
      loadDialProfileRef.current(id, { duplicate: false });
    } else if (duplicateId) {
      loadDialProfileRef.current(duplicateId, { duplicate: true });
    } else {
      setIsLoading(false);
      applyLoadedDataRef.current(deepClone(DEFAULT_FORM_DATA));
    }
  }, [duplicateId, id, isEditMode]); // Only depend on id and isEditMode to prevent infinite loops

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = searchParams.get('_reset');

    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      !duplicateId &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      const reset = deepClone(DEFAULT_FORM_DATA);
      setFormData(reset);
      setOriginalData(reset);
      setErrors({});

      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, duplicateId, searchParams]);

  const hasChanges = useMemo(() => {
    return (
      JSON.stringify(sanitizeFormData(formData)) !== JSON.stringify(sanitizeFormData(originalData))
    );
  }, [formData, originalData]);

  const handleFieldChange = useCallback(<K extends keyof DialProfileFormData>(
    field: K,
    value: DialProfileFormData[K],
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  }, [errors]);

  const handleRingbackPresetChange = useCallback(
    (value: string) => {
      if (value === 'custom') {
        handleFieldChange('ringbackTone', customRingbackValue || '');
      } else {
        handleFieldChange('ringbackTone', value as DialProfileFormData['ringbackTone']);
      }
    },
    [customRingbackValue, handleFieldChange],
  );

  const validateForm = useCallback(() => {
    const nextErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      nextErrors.name = t('dialProfiles.validation.nameRequired');
    }

    if (selectedRingbackPreset === 'custom') {
      if (!customRingbackValue.trim()) {
        nextErrors.ringbackTone = t('dialProfiles.validation.customRingbackRequired');
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [customRingbackValue, formData.name, selectedRingbackPreset, t]);

  const handleSubmit = useCallback(
    async (event?: React.FormEvent): Promise<DialProfile | undefined> => {
      event?.preventDefault();

      if (!validateForm()) {
        showError(t('dialProfiles.messages.validationError'));
        return undefined;
      }

      setIsSaving(true);
      try {
        const now = Date.now();
        if (now - lastSaveTimestamp.current < 1000) {
          return undefined;
        }
        lastSaveTimestamp.current = now;

        const sanitized = sanitizeFormData(formData);
        const payload = {
          name: sanitized.name,
          allowTransferBy: sanitized.allowTransferBy,
          allowParkBy: sanitized.allowParkBy,
          ringbackTone: sanitized.ringbackTone || 'default',
          callScreening: sanitized.callScreening,
          customOptions: sanitized.customOptions ? sanitized.customOptions : null,
          ringingTone: sanitized.ringingTone,
          description: sanitized.description || null,
          active: sanitized.active,
        };

        let result;
        if (isEditMode) {
          result = await updateDialProfile(id!, payload);
        } else {
          result = await createDialProfile(payload);
        }

        if (!result?.success) {
          throw new Error(t('dialProfiles.messages.saveError'));
        }

        showSuccess(
          isEditMode
            ? t('dialProfiles.messages.updateSuccess')
            : t('dialProfiles.messages.createSuccess'),
        );

        const savedData = sanitizeFormData({
          ...sanitized,
          ringbackTone: result?.data?.ringbackTone ?? result?.data?.ringback_tone ?? sanitized.ringbackTone,
        });

        setFormData(savedData);
        setOriginalData(deepClone(savedData));

        // Return saved data for useSaveWithAction
        if (result?.data?.id) {
          return result.data;
        }
        return undefined;
      } catch (error) {
        console.error('Failed to save dial profile', error);
        const message =
          error instanceof Error ? error.message : t('dialProfiles.messages.saveError');
        showError(message);
        return undefined;
      } finally {
        setIsSaving(false);
      }
    },
    [formData, hasChanges, id, isEditMode, showError, showInfo, showSuccess, t, validateForm],
  );

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: String(id) } as DialProfile;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: basePath,
    newPath: `${basePath}/new`,
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Dial Profiles use ID for navigation
      return `${basePath}/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formData
    }),
    storageKey: 'dialProfiles:saveAndNewData',
  });

  const handleCancel = useCallback(() => {
    if (isEditMode && id) {
      loadDialProfile(id, { duplicate: false });
      return;
    }
    const reset = deepClone(DEFAULT_FORM_DATA);
    setFormData(reset);
    setOriginalData(reset);
    setErrors({});
  }, [isEditMode, loadDialProfile]);

  const handleDelete = useCallback(() => {
    if (!isEditMode) {
      return;
    }
    setShowDeleteModal(true);
  }, [isEditMode]);

  const confirmDelete = useCallback(async () => {
    if (!id || id === 'new') {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteDialProfile(id!);

      showSuccess(t('dialProfiles.messages.deleteSuccess', { name: formData.name }));
      navigate(basePath);
    } catch (error) {
      console.error('Failed to delete dial profile', error);
      const message =
        error instanceof Error ? error.message : t('dialProfiles.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }, [basePath, formData.name, id, navigate, showError, showSuccess, t]);

  // Quick List functions
  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      // Load all profiles initially (no search param for client-side filtering)
      const result = await fetchDialProfiles({
        page: 1,
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc',
      });
      setQuickListProfiles(result.data || []);
    } catch (error) {
      showError(t('dialProfiles.messages.loadError'));
      setQuickListProfiles([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, [showError, t]);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList && quickListProfiles.length === 0 && !quickListLoadingRef.current) {
      void loadQuickList();
    }
  }, [showQuickList, quickListProfiles.length]); // Removed loadQuickList from dependencies to avoid loop

  // Client-side filtering
  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListProfiles;
    }

    return quickListProfiles.filter((profile) =>
      profile.name?.toLowerCase().includes(query) ||
      (profile.description || '').toLowerCase().includes(query)
    );
  }, [quickListProfiles, quickListSearch]);

  const handleQuickListSelect = useCallback((selectedProfile: DialProfile) => {
    setShowQuickList(false);
    setQuickListSearch('');
    navigate(`${basePath}/${selectedProfile.id}`);
  }, [basePath, navigate]);

  // Duplicate current profile from the fixed header action
  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToDialProfileDuplicate(navigate, id, isSettingsContext);
  }, [isEditMode, id, navigate, isSettingsContext]);

  return (
    <div className="p-6 pb-24">
      <ModuleFormHeader
        icon={<PhoneCall className="h-5 w-5 text-primary" />}
        title={pageTitle}
        backButtonText={t('common.list')}
        onBack={() => navigate(basePath)}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('dialProfiles.quickList.tooltip', 'Search'),
            onClick: () => {
              setShowQuickList(true);
              void loadQuickList();
            },
          },
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('common.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('dialProfiles.actions.add'),
            onClick: () => navigate(`${basePath}/new`),
          },
        ]}
      /><div className="mt-8 space-y-6">
        <DialProfileGeneralSection
          formData={formData}
          errors={errors}
          isLoading={isLoading}
          selectedRingbackPreset={selectedRingbackPreset}
          customRingbackValue={customRingbackValue}
          onFieldChange={handleFieldChange}
          onRingbackPresetChange={handleRingbackPresetChange}
          t={t}
          disabledName={isEditMode && isDefaultDialProfile}
        />

        <FormBox
          title={t('dialProfiles.sections.advanced')}
          description={t('dialProfiles.sections.advancedDescription')}
          icon={<RefreshCw className="h-5 w-5" />}
          isLoading={isLoading}
        >
          <div className="grid grid-cols-8 items-start gap-x-6 gap-y-4">
            <SimpleTooltip
              content={t('dialProfiles.fields.customOptions.tooltip')}
              className="col-span-2"
            >
              <Label htmlFor="customOptions" className="text-left">
                {t('dialProfiles.fields.customOptions.label')}
              </Label>
            </SimpleTooltip>
            <div className="col-span-6">
              <Textarea
                id="customOptions"
                value={formData.customOptions}
                onChange={(event) => handleFieldChange('customOptions', event.target.value)}
                placeholder={t('dialProfiles.fields.customOptions.placeholder')}
                rows={6}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {t('dialProfiles.fields.customOptions.help')}
              </p>
            </div>
          </div>
        </FormBox>
      </div>

      {/* Fixed Action Bar */}
      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode && !isDefaultDialProfile ? handleDelete : undefined}
        isEditMode={isEditMode}
        saveText={t('common.save')}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete')}
        saveDisabled={isSaving}
        isLoading={isSaving}
        useSaveDropdown={true}
        saveDropdownStorageKey="dialProfiles:saveActionPreference"
        hasChanges={hasChanges}
      />

      {/* Quick List Modal */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('dialProfiles.quickList.title')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(profile) => handleQuickListSelect(profile)}
        currentItemId={id}
        renderItem={(profile, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              handleQuickListSelect(profile);
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">{profile.name}</div>
            {profile.description && (
              <div className="text-sm text-muted-foreground mt-1">
                {profile.description}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-1">
              {profile.active ? t('common.enabled') : t('common.disabled')}
            </div>
          </button>
        )}
        searchPlaceholder={t('dialProfiles.quickList.searchPlaceholder')}
        emptyMessage={t('dialProfiles.quickList.empty')}
        noResultsMessage={t('dialProfiles.quickList.empty')}
        headerIcon={<PhoneCall className="h-5 w-5" />}
        minSearchChars={0}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('dialProfiles.messages.deleteTitle')}
        message={t('dialProfiles.messages.deleteConfirmation', { name: formData.name || '' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}


